'use client';

import { useMutation, useQuery } from '@tanstack/react-query';
import { Download, Plus, Trash2, Upload } from 'lucide-react';
import { useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { trpc, trpcClient } from '@/utils/trpc';
import { toast } from 'sonner';

interface RecurringRow {
  id: string;
  description: string;
  amount: string;
  type: 'debit' | 'credit';
  categoryId: string;
  frequency: 'daily' | 'weekly' | 'biweekly' | 'monthly' | 'quarterly' | 'yearly';
  dayOfMonth: string;
  isFixedExpense: boolean;
}

interface BulkRecurringFormProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function BulkRecurringForm({
  open,
  onClose,
  onSuccess,
}: BulkRecurringFormProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [rows, setRows] = useState<RecurringRow[]>([
    {
      id: crypto.randomUUID(),
      description: '',
      amount: '',
      type: 'debit',
      categoryId: '',
      frequency: 'monthly',
      dayOfMonth: '5',
      isFixedExpense: true,
    },
  ]);

  // Fetch categories
  const categories = useQuery(trpc.categories.list.queryOptions({}));

  // Bulk create mutation
  const bulkCreateMutation = useMutation({
    mutationFn: async (data: RecurringRow[]) => {
      try {
        // Create each recurring transaction using trpcClient
        const promises = data.map((row) => {
          const now = new Date();
          const dayOfMonth = Number.parseInt(row.dayOfMonth) || 1;

          // Calculate startDate: use LAST month so nextDate falls in current month
          // This ensures monthly recurring expenses show up in the current month
          const startDate = new Date(now.getFullYear(), now.getMonth() - 1, dayOfMonth);

          return trpcClient.recurring.create.mutate({
            amount: row.amount.replace(',', '.'),
            type: row.type,
            categoryId: row.categoryId || undefined,
            description: row.description,
            frequency: row.frequency,
            dayOfMonth: row.frequency === 'monthly' ? dayOfMonth : undefined,
            startDate: startDate.toISOString(),
            isActive: true,
            isFixedExpense: row.isFixedExpense,
          });
        });

        const results = await Promise.all(promises);
        return results;
      } catch (error) {
        console.error('Error creating recurring transactions:', error);
        throw error;
      }
    },
    onSuccess: () => {
      toast.success(`${rows.length} gastos fixos adicionados com sucesso!`);
      onSuccess();
      handleClose();
    },
    onError: (error: any) => {
      console.error('Bulk create error:', error);
      toast.error(error.message || 'Erro ao criar gastos fixos');
    },
  });

  const addRow = () => {
    setRows([
      ...rows,
      {
        id: crypto.randomUUID(),
        description: '',
        amount: '',
        type: 'debit',
        categoryId: '',
        frequency: 'monthly',
        dayOfMonth: '5',
        isFixedExpense: true,
      },
    ]);
  };

  const removeRow = (id: string) => {
    if (rows.length === 1) return;
    setRows(rows.filter((row) => row.id !== id));
  };

  const updateRow = (id: string, field: keyof RecurringRow, value: any) => {
    setRows(
      rows.map((row) => (row.id === id ? { ...row, [field]: value } : row))
    );
  };

  const downloadTemplate = () => {
    const csvContent = `Descrição,Valor,Tipo,Categoria,Frequência,Dia
Aluguel,1500,Gasto,,Mensal,5
Netflix,55.90,Gasto,,Mensal,10
Academia,89,Gasto,,Mensal,1
Luz,250,Gasto,,Mensal,15
Internet,99.90,Gasto,,Mensal,10`;

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'modelo-gastos-fixos.csv';
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      parseCSV(text);
    };
    reader.readAsText(file);

    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const parseCSV = (text: string) => {
    const lines = text.split('\n').filter(line => line.trim());
    if (lines.length < 2) {
      toast.error('CSV vazio ou inválido');
      return;
    }

    // Skip header
    const dataLines = lines.slice(1);

    const newRows: RecurringRow[] = dataLines.map(line => {
      // Simple CSV parser (handles basic cases)
      const values = line.split(',').map(v => v.trim().replace(/^["']|["']$/g, ''));

      const description = values[0] || '';
      const amount = values[1]?.replace('.', ',') || '';
      const typeStr = (values[2] || 'Gasto').toLowerCase();
      const type: 'debit' | 'credit' = typeStr.includes('receita') || typeStr.includes('credit') ? 'credit' : 'debit';
      const categoryName = values[3] || '';
      const frequencyStr = (values[4] || 'Mensal').toLowerCase();
      const dayOfMonth = values[5] || '5';

      // Map frequency
      let frequency: RecurringRow['frequency'] = 'monthly';
      if (frequencyStr.includes('diá') || frequencyStr.includes('daily')) frequency = 'daily';
      else if (frequencyStr.includes('seman') || frequencyStr.includes('weekly')) frequency = 'weekly';
      else if (frequencyStr.includes('quinzen') || frequencyStr.includes('biweekly')) frequency = 'biweekly';
      else if (frequencyStr.includes('trimest') || frequencyStr.includes('quarterly')) frequency = 'quarterly';
      else if (frequencyStr.includes('anual') || frequencyStr.includes('yearly')) frequency = 'yearly';

      // Try to find category by name
      let categoryId = '';
      if (categoryName && categories.data) {
        const foundCategory = categories.data.find(cat =>
          cat.name.toLowerCase().includes(categoryName.toLowerCase())
        );
        if (foundCategory) {
          categoryId = foundCategory.id;
        }
      }

      return {
        id: crypto.randomUUID(),
        description,
        amount,
        type,
        categoryId,
        frequency,
        dayOfMonth,
        isFixedExpense: true,
      };
    });

    setRows(newRows);
    toast.success(`${newRows.length} linhas importadas do CSV`);
  };

  const handleSubmit = () => {
    // Validate rows
    const validRows = rows.filter(
      (row) => {
        if (!row.description || !row.amount) return false;
        // Convert comma to dot for parseFloat validation
        const numericAmount = Number.parseFloat(row.amount.replace(',', '.'));
        return numericAmount > 0;
      }
    );

    if (validRows.length === 0) {
      toast.error('Adicione pelo menos um gasto fixo válido');
      return;
    }

    bulkCreateMutation.mutate(validRows);
  };

  const handleClose = () => {
    setRows([
      {
        id: crypto.randomUUID(),
        description: '',
        amount: '',
        type: 'debit',
        categoryId: '',
        frequency: 'monthly',
        dayOfMonth: '5',
        isFixedExpense: true,
      },
    ]);
    onClose();
  };

  return (
    <Dialog onOpenChange={handleClose} open={open}>
      <DialogContent className="max-h-[95vh] max-w-[95vw] overflow-y-auto sm:max-w-[1400px]">
        <DialogHeader>
          <DialogTitle>Adicionar Vários Gastos Fixos</DialogTitle>
          <DialogDescription>
            Preencha a tabela abaixo para adicionar múltiplos gastos fixos de uma vez.
            Todos serão marcados automaticamente como gastos fixos recorrentes.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* CSV Import/Export */}
          <div className="flex items-center gap-2">
            <input
              accept=".csv"
              className="hidden"
              onChange={handleFileUpload}
              ref={fileInputRef}
              type="file"
            />
            <Button
              className="gap-2"
              onClick={() => fileInputRef.current?.click()}
              type="button"
              variant="outline"
            >
              <Upload className="h-4 w-4" />
              Importar CSV
            </Button>
            <Button
              className="gap-2"
              onClick={downloadTemplate}
              type="button"
              variant="outline"
            >
              <Download className="h-4 w-4" />
              Baixar Modelo
            </Button>
            <span className="text-muted-foreground text-sm">
              Use o modelo CSV para importar vários gastos de uma vez
            </span>
          </div>

          {/* Table */}
          <div className="rounded-md border overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="min-w-[280px]">Descrição *</TableHead>
                  <TableHead className="min-w-[140px]">Valor *</TableHead>
                  <TableHead className="min-w-[120px]">Tipo</TableHead>
                  <TableHead className="min-w-[220px]">Categoria</TableHead>
                  <TableHead className="min-w-[160px]">Frequência</TableHead>
                  <TableHead className="min-w-[100px]">Dia</TableHead>
                  <TableHead className="w-[60px]" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row, index) => (
                  <TableRow key={row.id}>
                    {/* Description */}
                    <TableCell>
                      <Input
                        onChange={(e) =>
                          updateRow(row.id, 'description', e.target.value)
                        }
                        placeholder="Ex: Aluguel, Netflix..."
                        value={row.description}
                      />
                    </TableCell>

                    {/* Amount */}
                    <TableCell>
                      <Input
                        onChange={(e) =>
                          updateRow(row.id, 'amount', e.target.value)
                        }
                        placeholder="0,00"
                        value={row.amount}
                      />
                    </TableCell>

                    {/* Type */}
                    <TableCell>
                      <Select
                        onValueChange={(value) =>
                          updateRow(row.id, 'type', value)
                        }
                        value={row.type}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="debit">Gasto</SelectItem>
                          <SelectItem value="credit">Receita</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>

                    {/* Category */}
                    <TableCell>
                      <Select
                        onValueChange={(value) =>
                          updateRow(row.id, 'categoryId', value === 'none' ? '' : value)
                        }
                        value={row.categoryId || 'none'}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Categoria" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Sem categoria</SelectItem>
                          {categories.data?.map((category) => (
                            <SelectItem key={category.id} value={category.id}>
                              {category.icon} {category.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>

                    {/* Frequency */}
                    <TableCell>
                      <Select
                        onValueChange={(value: any) =>
                          updateRow(row.id, 'frequency', value)
                        }
                        value={row.frequency}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="daily">Diário</SelectItem>
                          <SelectItem value="weekly">Semanal</SelectItem>
                          <SelectItem value="biweekly">Quinzenal</SelectItem>
                          <SelectItem value="monthly">Mensal</SelectItem>
                          <SelectItem value="quarterly">Trimestral</SelectItem>
                          <SelectItem value="yearly">Anual</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>

                    {/* Day of Month */}
                    <TableCell>
                      {row.frequency === 'monthly' && (
                        <Input
                          max={31}
                          min={1}
                          onChange={(e) =>
                            updateRow(row.id, 'dayOfMonth', e.target.value)
                          }
                          placeholder="5"
                          type="number"
                          value={row.dayOfMonth}
                        />
                      )}
                    </TableCell>

                    {/* Remove */}
                    <TableCell>
                      <Button
                        disabled={rows.length === 1}
                        onClick={() => removeRow(row.id)}
                        size="sm"
                        type="button"
                        variant="ghost"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Add Row Button */}
          <Button
            className="w-full"
            onClick={addRow}
            type="button"
            variant="outline"
          >
            <Plus className="mr-2 h-4 w-4" />
            Adicionar Linha
          </Button>

          {/* Info */}
          <div className="rounded-md bg-blue-50 p-3 text-sm dark:bg-blue-950/30">
            <p className="font-medium text-blue-900 dark:text-blue-200">
              💡 Dica: Todos os gastos serão marcados como "Gastos Fixos" automaticamente
            </p>
            <p className="text-blue-700 text-xs dark:text-blue-300">
              Isso significa que eles não entrarão no cálculo do orçamento diário disponível.
            </p>
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <Button
              className="flex-1"
              onClick={handleClose}
              type="button"
              variant="outline"
            >
              Cancelar
            </Button>
            <Button
              className="flex-1"
              disabled={bulkCreateMutation.isPending}
              onClick={handleSubmit}
              type="button"
            >
              {bulkCreateMutation.isPending
                ? 'Salvando...'
                : `Salvar ${rows.filter((r) => r.description && r.amount).length} Gasto${rows.filter((r) => r.description && r.amount).length !== 1 ? 's' : ''}`}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
